// Canonical workspace-environment constants. Imported by both server code
// (env-scoped fhir_source queries, API key prefixes) and frontend UI (pickers,
// labels) so the "sandbox" / "production" literals live in exactly one place.

import type {
  AccountType,
  Designation,
  RequesterType,
} from "@/server/constants/persona";
import type { OrganizationAddress } from "@/server/schema/org";

export const ORG_ENVIRONMENT = {
  SANDBOX: "sandbox",
  PRODUCTION: "production",
} as const;

/** The environment a workspace operates in; scopes which fhir_sources it sees. */
export type OrgEnvironment = (typeof ORG_ENVIRONMENT)[keyof typeof ORG_ENVIRONMENT];

/**
 * Environment variable pointing an individual workspace's pickers and connect
 * page at sandbox health systems, so a local end-to-end test gets sandbox
 * sources and sandbox client ids together. It is scoped to
 * individual workspaces because they have no sandbox half, while an organization
 * workspace switches with the environment toggle instead.
 */
export const SANDBOX_SOURCES_FLAG = "SANDBOX_SOURCES";

/**
 * The name this flag shipped under before it covered every source-facing
 * surface. Still honoured, so an environment already carrying it keeps working
 * without an edit.
 */
export const SANDBOX_SOURCES_FLAG_LEGACY = "SANDBOX_DELETE_YOUR_DATA";

/**
 * The `organization` columns better-auth's payloads do not carry, read straight
 * from Postgres to decorate them into the shape `OrganizationSchema` declares.
 */
export type OrganizationDecoration = {
  workspaceGroupId: string | null;
  accountType: AccountType | null;
  requesterType: RequesterType | null;
};

/** The decoration used when an organization row could not be read back. */
export const EMPTY_ORGANIZATION_DECORATION: OrganizationDecoration = {
  workspaceGroupId: null,
  accountType: null,
  requesterType: null,
};

/** Inputs required to create and read back one workspace pair. */
export type CreateWorkspacePairParams = {
  ownerUserId: string;
  slugBase: string;
  name: string;
  logo: string | null;
  requesterType: RequesterType;
  address: OrganizationAddress;
  designation: Designation | null;
};

/** Inputs required to create an own-records workspace atomically. */
export type CreateIndividualWorkspaceParams = {
  ownerUserId: string;
  slugBase: string;
  name: string;
};

/** Inputs required to convert a workspace group persona atomically. */
export type SetWorkspacePersonaInput = {
  organizationId: string;
  ownerUserId: string;
  communicationEmail: string | null;
  accountType: AccountType;
  requesterType: RequesterType | null;
  address: OrganizationAddress | null;
  designation: Designation | null;
};

/** Email domain of Medblocks staff accounts. */
export const MEDBLOCKS_EMAIL_DOMAIN = "medblocks.com";

/** Organization name of the Medblocks-owned workspace, compared case-insensitively. */
export const MEDBLOCKS_ORG_NAME = "medblocks";
